import { cn } from '@/lib/utils'
import { fetchWorkflow } from '@/lib/workflows'
import { useQuery } from '@tanstack/react-query'
import { Loader2, PanelRightOpen, Workflow as WorkflowIcon } from 'lucide-react'
import { createContext, useContext } from 'react'
import { AgentAvatar, ChiefOrb } from '../AgentAvatar'

/** Lets anything in the chat open a workflow on the canvas panel next to the conversation. */
export const WorkflowPanelContext = createContext<{
  openId: number | null
  open: (id: number) => void
} | null>(null)

/** The card a workflow gets in the conversation: what it is, how far the build is, and a way back in. */
export function ChatWorkflowCard({
  id,
  prompt,
  refine
}: {
  id: number
  prompt: string
  refine: boolean
}) {
  const panel = useContext(WorkflowPanelContext)
  const wf = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => fetchWorkflow(id),
    refetchInterval: (q) => (q.state.data?.build_status === 'building' ? 1500 : false),
    retry: false
  })
  const data = wf.data
  const building = data?.build_status === 'building'
  const isOpen = panel?.openId === id

  if (wf.error)
    return (
      <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
        A workflow was built here, but it has since been deleted.
      </p>
    )

  return (
    <div className="mx-auto w-full max-w-xl rounded-[24px] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-start gap-3">
        {building ? (
          <ChiefOrb size="md" state="working" />
        ) : (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
            <WorkflowIcon className="size-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            {refine ? 'Changing a workflow' : 'Workflow'}
          </p>
          <p className="truncate font-bold text-neutral-900 dark:text-white">
            {!data
              ? 'Loading…'
              : building && data.graph.nodes.length === 0
                ? 'The team is building it…'
                : data.name}
          </p>
          <p className="mt-0.5 line-clamp-2 text-xs text-neutral-500 dark:text-neutral-400">
            {refine ? prompt : data?.description || prompt}
          </p>
        </div>
        {data && (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
              building
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                : data.build_status === 'asking'
                  ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                  : data.build_status === 'failed'
                  ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                  : data.enabled
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
            )}
          >
            {building && <Loader2 className="size-3 animate-spin" />}
            {building
              ? 'Building'
              : data.build_status === 'asking'
                ? 'Needs your answers'
                : data.build_status === 'failed'
                ? 'Build failed'
                : data.enabled
                  ? 'On'
                  : 'Ready to review'}
          </span>
        )}
      </div>

      {data && (
        <div className="mt-3 flex items-center gap-3">
          <div className="flex -space-x-1.5">
            {data.agents.slice(0, 5).map((key) => (
              <AgentAvatar
                key={key}
                agentKey={key}
                size="xs"
                state={building ? 'working' : 'idle'}
                className="ring-2 ring-white dark:ring-neutral-900"
              />
            ))}
          </div>
          <span className="min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400">
            {data.trigger} · {data.steps} step{data.steps === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => panel?.open(id)}
            disabled={!panel || isOpen}
            className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-900 px-3.5 py-1.5 text-xs font-bold text-white transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
          >
            <PanelRightOpen className="size-3.5" />
            {isOpen ? 'Open' : 'Open canvas'}
          </button>
        </div>
      )}
    </div>
  )
}
